import Typography from "@mui/material/Typography";
import { FormattedNumber, useIntl } from "react-intl";

import Box from "@mui/material/Box";

import { TVehicle } from "types";
import { Tupel, getKfzSteuer } from "components";

interface IEmissions {
  vehicle: TVehicle;
}

export const Elektro: React.FC<IEmissions> = ({ vehicle }): JSX.Element => {
  const { formatMessage: t } = useIntl();
  const year = parseInt(t({ id: "unitPrices.year" }));
  const unitPrice = parseFloat(
    t({ id: "unitPrices.kraftstoff." + vehicle.kraftstoff.id })
  );
  const co2PriceLow = parseFloat(t({ id: "unitPrices.co2.co2PriceLow" }));
  const co2Price = parseFloat(t({ id: "unitPrices.co2.co2Price" }));
  const co2PriceHigh = parseFloat(t({ id: "unitPrices.co2.co2PriceHigh" }));
  return (
    <>
      <Tupel
        label="Energiekosten bei 15.000 km Jahresfahrleistung:"
        float={unitPrice * 15000 * (vehicle.wltp_kombiniert / 100)}
        unit="EUR/Jahr"
        boxProps={{ fontWeight: "bold", pb: 0 }}
        digits={2}
        divider={false}
      />
      <Typography variant="body2" color="black">
        (Kraftstoffpreis:&nbsp;
        <FormattedNumber value={unitPrice} minimumFractionDigits={1} />
        &nbsp;EUR/l, (Jahresdurchschnitt {year}))
      </Typography>
      {vehicle.wltp_co2_emissionen && (
        <>
          <Typography variant="body1" sx={{ fontWeight: "bold", mt: 1 }}>
            Mögliche CO&#8322;-Kosten über die nächsten 10 Jahre (15.000
            km/Jahr):
            <sup>2</sup>
          </Typography>
          <Tupel
            label={`• bei einem angenommenen mittleren durchschnittlichen CO₂-Preis von ${co2Price} EUR/t:`}
            float={
              co2Price *
              15000 *
              10 *
              (vehicle.wltp_co2_emissionen / 1000 / 1000)
            }
            force
            unit="EUR"
            boxProps={{ fontWeight: "bold", p: 0 }}
            digits={2}
            divider={false}
          />
          <Tupel
            label={`• bei einem angenommenen niedrigen durchschnittlichen CO₂-Preis von ${co2PriceLow} EUR/t:`}
            float={
              co2PriceLow *
              15000 *
              10 *
              (vehicle.wltp_co2_emissionen / 1000 / 1000)
            }
            force
            unit="EUR"
            boxProps={{ p: 0 }}
            digits={2}
            divider={false}
          />
          <Tupel
            label={`• bei einem angenommenen hohen durchschnittlichen CO₂-Preis von ${co2PriceHigh} EUR/t:`}
            float={
              co2PriceHigh *
              15000 *
              10 *
              (vehicle.wltp_co2_emissionen / 1000 / 1000)
            }
            force
            unit="EUR"
            boxProps={{ p: 0 }}
            digits={2}
            divider={false}
          />
          <Tupel
            label="Kraftfahrzeugsteuer:"
            float={getKfzSteuer({ vehicle }) || undefined}
            unit="EUR"
            force
            boxProps={{ fontWeight: "bold" }}
            digits={2}
            divider={false}
          />
        </>
      )}
      <Box sx={{ mt: 2, px: 1, border: 1, typography: "caption" }}>
        <p>
          Die Informationen erfolgen gemäß der
          Pkw-Energieverbrauchskennzeichnungsverordnung. Die angegebenen Werte
          wurden nach dem vorgeschriebenen Messverfahren WLTP (Worldwide
          harmonised Light-duty vehicles Test Procedures) ermittelt. Der
          Kraftstoffverbrauch und der CO₂-Ausstoß eines Pkw sind nicht nur von
          der effizienten Ausnutzung des Kraftstoffs durch den Pkw, sondern auch
          vom Fahrstil und anderen nichttechnischen Faktoren abhängig. CO₂, ist
          das für die Erderwärmung hauptsächlich verantwortliche Treibhausgas.
          <br />
          Ein Leitfaden über den Kraftstoffverbrauch und die CO₂-Emissionen
          aller in Deutschland angebotenen neuen Pkw-Modelle ist unentgeltlich
          einsehbar an jedem Verkaufsort in Deutschland, an dem neue Pkw
          ausgestellt oder angeboten werden. Der Leitfaden ist auch hier
          abrufbar: https://www.dat.de/co2/
        </p>
        <p>
          <sup>1</sup> Es werden nur die CO₂-Emissionen angegeben, die durch den
          Betrieb des Pkw entstehen. CO₂-Emissionen, die durch die Produktion
          und Bereitstellung des Pkw sowie des Kraftstoffes bzw. der
          Energieträger entstehen oder vermieden werden, werden bei der
          Ermittlung der CO₂-Emissionen gemäß WLTP nicht berücksichtigt.
        </p>
        <p>
          <sup>2</sup> Aufgrund der CO,-Bepreisung sind künftig Erhöhungen der
          Kraftstoffkosten möglich. Die künftige CO,-Preisentwicklung ist
          unsicher, daher werden die möglichen CO₂-Kosten anhand von drei
          angenommenen CO₂-Preisen für den Zeitraum 2025 bis 2034 berechnet. Die
          tatsächlichen CO₂-Preise können sowohl höher als auch niedriger als in
          den hier zugrundeliegenden Modellrechnungen ausfallen. Die CO,-Kosten
          sind beim Tanken mit den Kraftstoffkosten zu bezahlen. Weitere
          Informationen unter www.alternativ-mobil.info.
        </p>
      </Box>
    </>
  );
};

export default Elektro;
